import React, { useState, useEffect } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { useToast } from "src/components/ui/toast"
import { TeamForm } from "./TeamForm"
import { useCreateTeam, useUpdateTeam } from "../hooks/useTeams"
import { fetchTeam } from "../api/teamsApi"

export function TeamDrawer({ open, onClose, team, onSuccess }) {
  const isEdit = !!team
  const { toast } = useToast()
  const createHook = useCreateTeam()
  const updateHook = useUpdateTeam()
  const isPending = createHook.isPending || updateHook.isPending

  // The list row lacks members; fetch the full team to hydrate the Members tab.
  const [hydrated, setHydrated] = useState(null)

  useEffect(() => {
    if (!open) { setHydrated(null); return }
    if (!team?.id) { setHydrated(null); return }
    let cancelled = false
    fetchTeam(team.id)
      .then((full) => !cancelled && setHydrated(full))
      .catch(() => !cancelled && setHydrated(team)) // fall back to the row
    return () => { cancelled = true }
  }, [open, team?.id])

  async function handleSubmit(payload) {
    try {
      if (isEdit) {
        await updateHook.mutate(team.id, payload)
        toast({ title: "Team updated", description: payload.name })
      } else {
        await createHook.mutate(payload)
        toast({ title: "Team created", description: payload.name })
      }
      onSuccess?.()
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : (errors?.message || "Something went wrong")
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  // For edit, wait until hydrated so the Members tab shows current members.
  const formTeam = isEdit ? hydrated : null
  const ready = !isEdit || !!hydrated

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit team" : "New team"}</SheetTitle>
          <SheetDescription>
            {isEdit ? "Update this team's details and members." : "Group members of your organization into a team."}
          </SheetDescription>
        </SheetHeader>
        {ready ? (
          <TeamForm team={formTeam} onSubmit={handleSubmit} onCancel={onClose} isPending={isPending} />
        ) : (
          <div className="flex flex-1 items-center justify-center text-[13px] text-slate-400">Loading…</div>
        )}
      </SheetContent>
    </Sheet>
  )
}
